import type { FeatureZoomOverride } from "./featureZoomOverrideTypes";

// Per-part zoom overrides for visualizerType "bvLook" — see the shape/unit docs in
// ../featureGroupZoomDefaults.ts (getFeatureZoomOverride).
//
// The `scale` values below (2026-08-15) were bulk-ported from every
// scss/bv/responsive-canvas-bvLook.scss per-part `scale(X)` value, collapsed to whichever non-1
// scale showed up most often across that part's layouts/scenes where it varied. Use
// `byLayoutType` (see `valve_trim`/`diverter_trim`/`hand_shower_kit`/`shower_wall` below) to split
// one back out if a part needs different zoom per room layout.
//
// `walk_in_tub` is intentionally left as manually tuned (not the mechanically-ported value) —
// don't overwrite it when re-running the port.
export function getBvLookFeatureZoomOverride(partId: string): FeatureZoomOverride | undefined {
  switch (partId) {
    case "left_wall":
    case "right_wall":
    case "front_wall":
    case "back_wall":
    case "split_wall":
    case "left_wall_splitter":
    case "shower_door":
      return { scale: 1 };
    case "bathtub_body":
    case "bathtub_fixture":
    case "body_spray":
    case "grab_bar":
    case "paper_holder":
    case "shampoo_shelf":
    case "shower_hose":
    case "shower_pod":
    case "shower_system":
    case "shower_trim_kit":
    case "small_corner_shelf":
    case "soap_dish":
    case "toilet":
    case "towel_bar":
    case "vanity_backsplash":
    case "vanity_faucet":
    case "wall_union":
      return { scale: 1.3 };
    case "vanity_sink":
    case "vanity_countertop":
      return {
      scale: 1.3,
      byLayoutType: {
        18: { offsetX: 83, offsetY: 70, scale: 1.3 },
        19: { offsetX: 37, offsetY: 70, scale: 1.3 },
        10: { offsetX: 10, offsetY: 70, scale: 1.3 },
        11: { offsetX: 15, offsetY: 70, scale: 1.3 },
      },
    };
    case "vanity_cabinet":
      return {
        scale: 1.2,
      };
    case "bathtub_liner":
    case "ceiling_panels":
    case "corner_caddie":
    case "corner_shelf":
    case "jetted_tub":
    case "large_corner_caddie":
    case "large_corner_seat":
    case "shower_arm":
    case "shower_fixture":
    case "shower_head":
    case "shower_rod":
    case "threshold":
    case "tub_common":
    case "vanity_mirror":
    case "window_kits":
    case "shower_floor":
    case "shower_curb":
    case "shower_pan_combo":
    case "hand_shower_holder":
      return { scale: 1.2 };
    case "robe_hook":
      return { scale: 1.5 };
    case "towel_ring":
      return { scale: 1.4 };
    // manually tuned (not the mechanically-ported value) — don't overwrite when re-running the port.
    case "walk_in_tub":
    case "replacement_tub":
      return {
        scale: 1.1,
        byLayoutType: {
          19: { offsetX: 80, offsetY: 80, scale: 1.1 },
        },
      };
    case "hand_shower_kit":
    case "valve_trim":
    case "diverter_trim":
    case "shower_seats":
    case "seat_pad":
      return {
        scale: 1.3,
        byLayoutType: {
          // 18: { offsetX: 50, offsetY: 50, scale: 1.2 }, // 50,50 = canvas-center
          // 19: { offsetX: 80, offsetY: 60, scale: 1.1 },
          18: { offsetY: 50, scale: 1.2 },
          19: { offsetY: 60, scale: 1.1 },
        },
      };
    case "replacement_tub_fixture":
      return {
        scale: 1.2,
        byLayoutType: {
          18: { offsetX: 50, offsetY: 27, scale: 1.1 },
          19: { offsetX: 80, offsetY: 35, scale: 1.1 },
        },
      };
    case "shower_wall":
      return {
        scale: 1.1,
        byLayoutType: {
          18: { offsetX: 50, offsetY: 50, scale: 1.1 },
          19: { offsetY: 50, scale: 1.1 },
        },
      };
    case "bathroom_floor":
      return {
        scale: 1,
        byLayoutType: {
          18: { offsetY: 100, scale: 1 },
          19: { offsetY: 100, scale: 1 },
          11: { offsetY: 100, scale: 1 },
          10: { offsetY: 100, scale: 1 },
        },
      };
    default:
      return undefined;
  }
}
